import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { formatDateTime as _formatDateTime, formatDateOnly as _formatDateOnly, formatRelativeTime as _formatRelativeTime, getBrowserTimezone, clearFormatterCache } from './dateUtils';

/**
 * TimezoneContext
 * 
 * Provides reactive timezone state to the component tree.
 * Reads initial value from ProfileCache (localStorage).
 * Triggers re-render in all consuming components when timezone changes.
 * Syncs across browser tabs via the `storage` event.
 */

const TimezoneContext = createContext(null);

/**
 * Read timezone from ProfileCache in localStorage.
 * Returns null if not found (will fall back to browser auto-detect).
 */
const readTimezoneFromCache = () => {
  try {
    const cacheKeys = ['profile_cache_iam-console', 'profile_cache_admin-console'];
    for (const key of cacheKeys) {
      const cached = localStorage.getItem(key);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.data?.timezone) {
          return parsed.data.timezone;
        }
      }
    }
  } catch {
    // Ignore parse errors
  }
  return null;
};

export const TimezoneProvider = ({ children }) => {
  const [timezone, setTimezoneState] = useState(() => {
    return readTimezoneFromCache() || getBrowserTimezone();
  });

  // Update timezone in state (called after API save + ProfileCache update)
  const setTimezone = useCallback((newTimezone) => {
    clearFormatterCache();
    setTimezoneState(newTimezone || getBrowserTimezone());
  }, []);

  // Sync timezone when ProfileCache changes in another tab
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key && event.key.startsWith('profile_cache_')) {
        const cachedTz = readTimezoneFromCache();
        if (cachedTz && cachedTz !== timezone) {
          clearFormatterCache();
          setTimezoneState(cachedTz);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [timezone]);

  // Wrap formatting functions to use current timezone from state
  const formatDateTime = useCallback((isoString) => {
    return _formatDateTime(isoString, timezone);
  }, [timezone]);

  const formatDateOnly = useCallback((isoString) => {
    return _formatDateOnly(isoString, timezone);
  }, [timezone]);

  const formatRelativeTime = useCallback((isoString) => {
    return _formatRelativeTime(isoString);
  }, []);

  const value = {
    timezone,
    setTimezone,
    formatDateTime,
    formatDateOnly,
    formatRelativeTime,
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};

/**
 * Hook to access timezone context.
 * 
 * @returns {{ timezone: string, setTimezone: (tz: string) => void, formatDateTime: (iso: string) => string, formatDateOnly: (iso: string) => string, formatRelativeTime: (iso: string) => string }}
 */
export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    // Fallback for components rendered outside the provider (safety net)
    return {
      timezone: getBrowserTimezone(),
      setTimezone: () => {},
      formatDateTime: (iso) => _formatDateTime(iso),
      formatDateOnly: (iso) => _formatDateOnly(iso),
      formatRelativeTime: (iso) => _formatRelativeTime(iso),
    };
  }
  return context;
};

export default TimezoneContext;
